import React, { useContext, useState } from 'react';
import { Loader, Play, Music, Image } from 'lucide-react';
import Context from '../../../../Context/Context';

// Utility function to clean and encode thumbnail URLs
const sanitizeThumbnailUrl = (url) => {
  if (!url) return null;
  try {
    // First, decode any existing encoding
    const decodedUrl = decodeURIComponent(url);
    // Then properly encode spaces and special characters
    return decodedUrl
      .replace(/\s+/g, '%20')
      .replace(/[^\w\-._~:/?#[\]@!$&'()*+,;=%]/g, encodeURIComponent);
  } catch (error) {
    console.error('Error cleaning thumbnail URL:', error);
    return url; // Return original if cleaning fails
  }
};

const Playlist = ({ onSelectPlaylist, selectedPlaylist }) => {
  const {
    playlists,
    errorPlaylist,
    loadingPlaylist,
    isDarkMode = true,
  } = useContext(Context);

  const [thumbnailErrors, setThumbnailErrors] = useState({});
  const [thumbnailLoaded, setThumbnailLoaded] = useState({});

  const handleThumbnailError = (playlistIndex) => {
    setThumbnailErrors(prev => ({
      ...prev,
      [playlistIndex]: true
    }));
  };

  const handleThumbnailLoad = (playlistIndex) => {
    setThumbnailLoaded(prev => ({
      ...prev,
      [playlistIndex]: true
    }));
    // Clear error state if image loads successfully
    if (thumbnailErrors[playlistIndex]) {
      setThumbnailErrors(prev => ({
        ...prev,
        [playlistIndex]: false
      }));
    }
  };

  // Enhanced thumbnail component for playlists
  const PlaylistThumbnail = ({ playlist, index }) => {
    const thumbnailUrl = playlist.videos[0]?.thumbnailUrl;
    const hasThumbnailError = thumbnailErrors[index];
    const isThumbnailLoaded = thumbnailLoaded[index];

    const cleanThumbnailUrl = sanitizeThumbnailUrl(thumbnailUrl);
    
    if (!cleanThumbnailUrl) {
      return (
        <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${
          isDarkMode ? 'bg-[#2a2a2a]' : 'bg-gradient-to-br from-gray-200 to-gray-300'
        }`}>
          <div className="text-center">
            <Music className={`w-6 h-6 mx-auto mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No Thumbnail</p>
          </div>
        </div>
      );
    }

    return (
      <div className="relative w-16 h-16 overflow-hidden rounded-lg">
        {/* Main thumbnail image */}
        <img
          src={cleanThumbnailUrl}
          alt={playlist.playlistName}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          className="transition-all duration-300 hover:scale-105"
          onLoad={() => handleThumbnailLoad(index)}
          onError={() => {
            console.error('Playlist thumbnail failed to load for:', playlist.playlistName, 'URL:', cleanThumbnailUrl);
            handleThumbnailError(index);
          }}
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
        
        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
          <div className="bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg transform scale-75 hover:scale-90 transition-transform duration-200">
            <Play className="w-4 h-4 text-gray-800" fill="currentColor" />
          </div>
        </div>
        
        {/* Loading state */}
        {!isThumbnailLoaded && !hasThumbnailError && (
          <div className={`absolute inset-0 flex items-center justify-center ${isDarkMode ? 'bg-[#2a2a2a]' : 'bg-gray-200'}`}>
            <div className={`animate-spin rounded-full h-6 w-6 border-b-2 ${isDarkMode ? 'border-[#FDCF08]' : 'border-blue-600'}`}></div>
          </div>
        )}
        
        {/* Error state */}
        {hasThumbnailError && (
          <div className={`absolute inset-0 flex items-center justify-center ${
            isDarkMode ? 'bg-[#2a2a2a]' : 'bg-gradient-to-br from-gray-200 to-gray-300'
          }`}>
            <div className="text-center">
              <Image className={`w-4 h-4 mx-auto mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Failed</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loadingPlaylist) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader className={`animate-spin mb-4 ${isDarkMode ? 'text-[#FDCF08]' : 'text-blue-600'}`} />
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Loading playlists...</p>
      </div>
    );
  }

  if (errorPlaylist) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Music className={`mb-4 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
        <p className={`text-center ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>{errorPlaylist}</p>
      </div>
    );
  }

  if (playlists.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Music className={`mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        <p className={`text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No playlists available</p>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-4 space-y-2 md:space-y-3">
      {playlists.map((playlist, index) => {
        const isSelected = selectedPlaylist?.playlistName === playlist.playlistName;
        
        return (
          <div
            key={index}
            onClick={() => onSelectPlaylist(playlist)}
           className={`
               group relative cursor-pointer rounded-lg p-3 transition-all duration-200
               ${isSelected 
                 ? isDarkMode
                   ? 'bg-[#2a2a2a] border-2 border-[#FDCF08]'
                   : 'bg-[#FDCF08]/10 border-2 border-[#FDCF08]'
                 : isDarkMode
                 ? 'bg-[#161616] border-0 hover:border-[#FDCF08] hover:bg-[#1a1a1a]'
                 : 'bg-white border border-gray-200 hover:border-[#FDCF08]'
               }
             `}
          >
            <div className="flex items-center space-x-3">
              {/* Enhanced Thumbnail */}
              <div className="relative flex-shrink-0">
                <PlaylistThumbnail playlist={playlist} index={index} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className={`
                  font-semibold text-sm mb-0.5 truncate
                  ${isSelected 
                    ? isDarkMode ? 'text-[#FDCF08]' : 'text-[#FDCF08]'
                    : isDarkMode ? 'text-white' : 'text-gray-900'
                  }
                `}>
                  {playlist.playlistName}
                </h3>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {playlist.videos.length} video{playlist.videos.length !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Selection indicator */}
              {isSelected && (
                <div className="w-2 h-2 bg-[#FDCF08] rounded-full flex-shrink-0" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Playlist;